import { BrButton, BrMessage } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import CustomConfirmer from 'components/customConfirmer/customConfirmer';
import CustomLoading from 'components/customLoading/customLoading';
import ErrorMessage from 'components/errorMessage/errorMessage';
import ModalExportacaoGuias from 'components/modalExportacaoGuias/modalExportacaoGuias';
import { useAuth } from 'hooks/useAuth';
import { useNotificacao } from 'hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IOrderField, IReceberListagemGuiasRecolhimento } from 'interfaces/IGuiaRecolhimento';
import { IIdentificacaoBasica } from 'interfaces/IIdentificacaoBasica';
import { useBackEndRequests } from 'pages/components/lib/backEndRequests';
import React, { useEffect, useState } from 'react';
import { Button, Modal, Radio } from 'react-dsgov';
import { useNavigate } from 'react-router';
import { Tooltip } from 'react-tooltip';
import { isCPForCNPJ } from 'utils/formatCNPJOrCPF';
import FiltrosLista from './components/filtrosLista/filtrosLista';
import ListaPaginada from './components/listaPaginada/listaPaginada';
import styles from './ListaGuias.module.scss';

const ListaGuias = () => {
  const DEFAULT_PAGE_SIZE = 10;
  const PAGE_SIZE_OPTIONS = [5, 10, 20];
  const [isModalOpened, setIsModalOpened] = React.useState(false);
  const [isModalExportarGuiasOpened, setIsModalExportarGuiasOpened] = React.useState(false);
  const [isModalExcluirGuiaOpened, setIsModalExcluirGuiaOpened] = React.useState<string | null>(null);
  const [selectedRadio, setSelectedRadio] = React.useState<string>('');
  const [filtrosGuiaRecolhimento, setFiltrosGuiaRecolhimento] = React.useState<string>('');

  const [order, setOrder] = React.useState<string>('status:ASC');
  const [orderArray, setOrderArray] = React.useState<IOrderField[]>([{ field: 'status', direction: 'ASC' }]);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageCount, setPageCount] = useState(0);
  const [pageItemCount, setPageItemCount] = useState(0);

  const auth = useAuth();
  const mostrarNotificacao = useNotificacao();

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');

  const navigate = useNavigate();

  const confirmGerarArrecadacao = async () => {
    if (selectedRadio) {
      navigate('/' + selectedRadio + `?id=${crypto.randomUUID()}`);
    }
  };

  const { getListaGuiasRecolhimento, getIdentificacaoBasica, deleteGuiaRecolhimento } = useBackEndRequests();

  const {
    data: declaranteData,
    isFetching: isLoadingDeclarante,
    error: errorDeclarante,
    refetch: refetchDeclarante,
  } = useQuery<IIdentificacaoBasica, IErrorWithSentryId>({
    queryKey: ['idBasicaQuery', userCpfCnpj],
    queryFn: () => getIdentificacaoBasica(userCpfCnpj || ''),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const {
    data: listaGuiasRecolhimentoData,
    isFetching: isLoadingListaGuiasRecolhimento,
    error: errorListaGuiasRecolhimento,
    refetch: refetchListaGuiasRecolhimento,
  } = useQuery<IReceberListagemGuiasRecolhimento, IErrorWithSentryId>({
    queryKey: ['listaGuiasRecolhimentoQuery', userCpfCnpj, filtrosGuiaRecolhimento, pageNumber, pageSize, order],
    queryFn: () => getListaGuiasRecolhimento(filtrosGuiaRecolhimento, pageNumber - 1, pageSize, order),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !!userCpfCnpj && auth.isAuthenticated,
  });

  useEffect(() => {
    if (declaranteData) {
      sessionStorage.setItem('isPrimeiroAdquirente', declaranteData.isPrimeiroAdquirente ? 'true' : 'false');
      sessionStorage.setItem('isPessoaNaoCadastrada', declaranteData.isCadastrado ? 'false' : 'true');
      sessionStorage.setItem(
        'naturezaDocumentacao',
        declaranteData.naturezaDocumentoIdentificacao === 2 ? 'filial' : 'nao-filial'
      );
    } else {
      sessionStorage.setItem('isPessoaNaoCadastrada', 'false');
    }
  }, [declaranteData]);

  useEffect(() => {
    if (listaGuiasRecolhimentoData) {
      setPageItemCount(listaGuiasRecolhimentoData.page.totalElements);
      setPageCount(listaGuiasRecolhimentoData.page.totalPages);
    }
  }, [listaGuiasRecolhimentoData]);

  useEffect(() => {
    refetchListaGuiasRecolhimento();
  }, [filtrosGuiaRecolhimento, pageNumber, pageSize, order]);

  useEffect(() => {
    setOrder(orderArray.map((order) => `${order.field}:${order.direction}`).join(','));
  }, [orderArray]);

  const onPageNumberChange = (number: number) => {
    setPageNumber(number);
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const renderContent = () => {
    if (isLoadingDeclarante) {
      return <CustomLoading label='Carregando...' />;
    } else if (errorDeclarante) {
      return (
        <ErrorMessage
          errorMessage={errorDeclarante.response?.statusText || errorDeclarante.message}
          errorDescription='Ocorreu um erro ao recuperar os dados do declarante. Por favor, tente novamente.'
          error={errorDeclarante}
          onClickFunction={() => refetchDeclarante()}
          buttonMessage='Tente Novamente'
          noBorder
        />
      );
    } else if (declaranteData?.isCadastrado === false) {
      return (
        <ErrorMessage
          errorMessage='Declarante não cadastrado no sistema'
          errorTitle={`${isCPForCNPJ(userCpfCnpj ?? '')} não cadastrado no sistema`}
          errorDescription={
            <>
              O {isCPForCNPJ(userCpfCnpj ?? '')} informado como declarante não está cadastrado no sistema. Selecione
              outro representado no combo acima "CPF/CNPJ Representado" ou, caso ache que isto é um erro, verifique com
              a Agência Nacional de Mineração (ANM) se o cadastro está correto. Após o cadastro,{' '}
              <b>aguarde pelo menos 1 dia para a validação dos dados.</b> Para mais informações, clique no botão abaixo
              e acesse o sistema da ANM.
            </>
          }
          onClickFunction={() => {
            window.location.href =
              'https://www.gov.br/anm/pt-br/assuntos/exploracao-mineral/Cadastro-para-Requerimentos';
          }}
          buttonMessage='Sistema ANM'
          noBorder
        />
      );
    } else if (declaranteData?.naturezaDocumentoIdentificacao === 2) {
      return (
        <ErrorMessage
          errorTitle='Guias de Recolhimento de Titular ou Arrendatário ou Primeiro Adquirente devem ser realizadas pelo CNPJ matriz'
          errorDescription={
            <>
              A emissão da guia de recolhimento da CFEM para <strong>Pessoas Jurídicas</strong> deve ser consolidada e
              enviada exclusivamente pelo <strong>CNPJ do estabelecimento matriz</strong>. Seu acesso atual está
              operando em nome de um CNPJ de filial. Para prosseguir, retorne e selecione a opção{' '}
              <strong>CNPJ Representado</strong> para operar em nome da matriz. Caso o CNPJ da matriz não esteja
              aparecendo como uma opção, verifique se seu acesso gov.br (Nível Prata ou superior) está corretamente
              associado ao e-CNPJ da matriz na plataforma gov.br. A gestão de colaboradores e a associação de novos
              representantes são realizadas pelo responsável do e-CNPJ, conforme orientações disponíveis na página:{' '}
              <a
                href='https://www.gov.br/governodigital/pt-br/acessibilidade-e-usuario/atendimento-gov.br/duvidas-na-conta-gov.br/duvidas-na-vinculacao-de-cnpj-no-gov.br/como-gerenciar-colaboradores'
                target='_blank'
                rel='noopener noreferrer'
              >
                Como Gerenciar Colaboradores.
              </a>
            </>
          }
          noBorder
        />
      );
    } else {
      return (
        <>
          <Modal title='Excluir Guia de Recolhimento' useScrim modalOpened={isModalExcluirGuiaOpened !== null}>
            <Modal.Body>
              Deseja mesmo deletar <strong>TODA</strong> a guia de recolhimento? Esse processo{' '}
              <strong>não tem volta</strong> e excluirá todas as bases de cálculo vinculadas a essa guia.
            </Modal.Body>
            <Modal.Footer justify-content='end'>
              <Button secondary small m={2} onClick={() => setIsModalExcluirGuiaOpened(null)}>
                Cancelar
              </Button>
              <CustomConfirmer asyncCall={() => excluirGuiaRecolhimento(isModalExcluirGuiaOpened!)}>
                Confirmar
              </CustomConfirmer>
            </Modal.Footer>
          </Modal>
          <div className={styles.titleContainer}>
            <h4>Recolhimento da CFEM</h4>
            <BrButton
              size='large'
              primary
              icon='plus-circle'
              className={styles.criarButton}
              onClick={() => setIsModalOpened(true)}
            >
              Emitir nova guia de recolhimento da CFEM
            </BrButton>
          </div>
          <div className={styles.guiasContainer}>
            <div className={styles.acoesListaContainer}>
              <div className={styles.tableTitle}> Lista de Guias de Recolhimento</div>
              <BrButton
                icon='download'
                size='small'
                secondary
                onClick={() => {
                  setIsModalExportarGuiasOpened(!isModalExportarGuiasOpened);
                }}
              >
                Exportar guias de recolhimento
              </BrButton>
            </div>
            <ModalExportacaoGuias
              title={'Exportar Guias de Recolhimento'}
              isModalOpened={isModalExportarGuiasOpened}
              setIsModalOpened={setIsModalExportarGuiasOpened}
            ></ModalExportacaoGuias>
            <FiltrosLista setFiltrosGuiaRecolhimento={setFiltrosGuiaRecolhimento} setPageNumber={setPageNumber} />
            <ListaPaginada
              listaGuiasRecolhimentoData={listaGuiasRecolhimentoData}
              pageCount={pageCount}
              itemCount={pageItemCount}
              selectedPage={pageNumber}
              selectedPageSize={pageSize}
              onChangePage={onPageNumberChange}
              onChangeSizePage={onPageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              showPageCombo={true}
              setOrderArray={setOrderArray}
              orderArray={orderArray}
              isLoading={isLoadingListaGuiasRecolhimento}
              error={errorListaGuiasRecolhimento}
              refetch={refetchListaGuiasRecolhimento}
              setIsModalExcluirGuiaOpened={setIsModalExcluirGuiaOpened}
            />
          </div>
        </>
      );
    }
  };

  const excluirGuiaRecolhimento = async (guiaRecolhimentoId: string) => {
    await deleteGuiaRecolhimento(guiaRecolhimentoId)
      .then(() => {
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Guia de recolhimento excluída com sucesso',
          });
        }, 100);

        setIsModalExcluirGuiaOpened(null);
        refetchListaGuiasRecolhimento();
      })
      .catch((err) => {
        setIsModalExcluirGuiaOpened(null);
        if (err.status === 404) {
          setIsModalExcluirGuiaOpened(null);
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao excluir a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
          });
        }
      });
  };

  return (
    <div className={styles.container}>
      <Modal
        title='Nova guia de recolhimento da CFEM'
        useScrim
        showCloseButton
        onCloseButtonClick={() => {
          setIsModalOpened(false);
        }}
        modalOpened={isModalOpened}
        className={styles.modal}
      >
        <div className={styles.contentContainer}>
          {declaranteData?.isPrimeiroAdquirente === false && (
            <BrMessage
              status='warning'
              message={
                <span>
                  Para acessar a seção Primeiro Adquirente, atualize os dados como primeiro adquirente no{' '}
                  <a href='https://app.anm.gov.br/sdc/' target='_blank' rel='noopener noreferrer'>
                    Sistema de Dados Cadastrais
                  </a>{' '}
                  e aguarde 1 dia para a validação.
                </span>
              }
            />
          )}
          Selecione um tipo de guia de recolhimento para continuar:
          <div className={styles.itensContainer}>
            <Radio
              checked={selectedRadio === 'titular-ou-arrendatario'}
              id='radio-titular-ou-arrendatario'
              label={
                <div
                  className={styles.radioContent}
                  data-tooltip-id='radio-tooltip'
                  data-tooltip-html='<div className={styles.tooltipText}>
                  <strong>Titular</strong> é a pessoa que detém os direitos minerários e exerce a atividade de mineração em seu próprio nome.
                  <br/><strong>Arrendatário</strong> é a pessoa que exerce, a título oneroso ou gratuito, a atividade de exploração de recursos minerais com base nos direitos do titular original.
                  </div>'
                  data-tooltip-place={'top'}
                >
                  <i className={`fas fa-file-alt fa-3x ${styles.icon}`} />
                  <span className={styles.radioLabel}>Titular ou Arrendatário</span>
                </div>
              }
              value='radio-titular-ou-arrendatario'
              name='radio-items'
              className={`${styles.radio} ${selectedRadio === 'titular-ou-arrendatario' ? styles.selected : ''}`}
              onClick={() => setSelectedRadio('titular-ou-arrendatario')}
            />
            <Radio
              checked={selectedRadio === 'primeiro-adquirente'}
              id='radio-primeiro-adquirente'
              disabled={declaranteData?.isPrimeiroAdquirente === false}
              label={
                <div
                  className={styles.radioContent}
                  data-tooltip-id='radio-tooltip'
                  data-tooltip-html='<div className={styles.tooltipText}>
                  O <strong>primeiro adquirente</strong> é a pessoa que realiza a primeira aquisição de um bem mineral extraído sob o regime de permissão de lavra garimpeira.
                  </div>'
                >
                  <i className={`fas fa-hand-holding-usd fa-3x ${styles.icon}`} />
                  <span className={styles.radioLabel}>Primeiro Adquirente</span>
                </div>
              }
              value='radio-primeiro-adquirente'
              name='radio-items'
              className={`${styles.radio} ${selectedRadio === 'primeiro-adquirente' ? styles.selected : ''}`}
              onClick={() => setSelectedRadio('primeiro-adquirente')}
            />
            <Radio
              checked={selectedRadio === 'hasta-publica'}
              id='radio-hasta-publica'
              disabled
              label={
                <div
                  className={styles.radioContent}
                  data-tooltip-id='radio-tooltip'
                  data-tooltip-html='<div className={styles.tooltipText}>
                  A <strong>arrematação em hasta pública</strong> é um procedimento de leilão público no qual são oferecidos bens minerais apreendidos. A pessoa (arrematante) que adquire o bem mineral ofertado em hasta pública fica obrigada ao pagamento da CFEM nos termos na Lei.
                  </div>'
                >
                  <i className={`fas fa-gavel fa-3x ${styles.icon}`} />
                  <span className={styles.radioLabel}>Arrematação em Hasta Pública</span>
                </div>
              }
              value='radio-hasta-publica'
              name='radio-items'
              className={`${styles.radio} ${selectedRadio === 'hasta-publica' ? styles.selected : ''}`}
              onClick={() => setSelectedRadio('hasta-publica')}
            />
          </div>
          <Tooltip id='radio-tooltip' className={styles.tooltip} variant='info' />
        </div>
        <div className={styles.footerContainer}>
          <Button
            secondary
            small
            m={2}
            onClick={() => {
              setIsModalOpened(false);
            }}
          >
            Cancelar
          </Button>
          <CustomConfirmer asyncCall={() => confirmGerarArrecadacao()}>Confirmar</CustomConfirmer>
        </div>
      </Modal>
      {renderContent()}
    </div>
  );
};

export default ListaGuias;
